import {
  InsightsAttributionWindowClick,
  InsightsAttributionWindowView,
  InsightsMetricOperationV2,
  InsightsFacebookSelectableDatePreset,
  InsightsFacebookCreativeType,
} from '@magicbrief/common';
import { BaseTranslation } from 'src/i18n/i18n-types';

export const timePeriod: Record<
  InsightsFacebookSelectableDatePreset | 'custom',
  string
> = {
  today: 'Today',
  yesterday: 'Yesterday',
  last_3d: 'Last 3 Days',
  last_7d: 'Last 7 Days',
  last_14d: 'Last 14 Days',
  last_28d: 'Last 28 Days',
  last_30d: 'Last 30 Days',
  last_90d: 'Last 90 Days',
  last_365d: 'Last 365 Days',
  this_week_sun_today: 'This Week',
  last_week_sun_sat: 'Last Week',
  this_month: 'This Month',
  last_month: 'Last Month',
  last_quarter: 'Last Quarter',
  this_year: 'This Year',
  last_year: 'Last Year',
  maximum: 'All Time',
  custom: 'Custom',
};

const timePeriodTruncated: Record<
  InsightsFacebookSelectableDatePreset | 'custom',
  string
> = {
  today: 'Tdy',
  yesterday: 'Ysty',
  last_3d: '3d',
  last_7d: '7d',
  last_14d: '14d',
  last_28d: '28d',
  last_30d: '30d',
  last_90d: '90d',
  last_365d: '365d',
  this_week_sun_today: 'Wk',
  last_week_sun_sat: 'Lst Wk',
  this_month: 'Mnth',
  last_month: 'Lst Mnth',
  last_quarter: 'LstQtr',
  this_year: 'Yr',
  last_year: 'Lst Yr',
  maximum: 'All',
  custom: 'Cstm',
};

const effectiveStatus = {
  ACTIVE: 'Active',
  PAUSED: 'Paused',
  DELETED: 'Deleted',
  PENDING_REVIEW: 'Pending Review',
  DISAPPROVED: 'Disapproved',
  PREAPPROVED: 'Preapproved',
  PENDING_BILLING_INFO: 'Pending Billing Info',
  CAMPAIGN_PAUSED: 'Campaign Paused',
  ARCHIVED: 'Archived',
  ADSET_PAUSED: 'Ad Set Paused',
  IN_PROCESS: 'In Processing',
  WITH_ISSUES: 'Delivery Issues',
};

const attributionWindows: Record<
  InsightsAttributionWindowView | InsightsAttributionWindowClick,
  string
> = {
  '1d_click': '1 Day Click',
  '7d_click': '7 Day Click',
  '28d_click': '28 Day Click',
  skan_click: 'SKAN (Apple SKAdNetwork) Click',
  '1d_view': '1 Day View',
  '7d_view': '7 Day View',
  '28d_view': '28 Day View',
  '7d_view_first_conversion': '7 Day View (First Conversion)',
  '28d_view_first_conversion': '28 Day View (First Conversion)',
  '7d_view_all_conversions': '7 Day View (All Conversions)',
  '28d_view_all_conversions': '28 Day View (All Conversions)',
  skan_view: 'SKAN (Apple SKAdNetwork) View',
};

const creativeType: Record<InsightsFacebookCreativeType, string> = {
  image: 'Image',
  video: 'Video',
  carousel: 'Carousel',
  dynamic: 'Dynamic',
  unknown: 'Unknown',
};

export const insights: BaseTranslation = {
  accounts: {
    accountSelect: {
      title: 'Select ad accounts',
      description:
        "Select the ad accounts you'd like to connect with Insights.\nNeed to add or remove ad accounts? Visit your Workplace Settings.",
      availableTitle: 'Available ad accounts',
      status: {
        notAdded: {
          ctaText: 'Not connected',
          tooltipText:
            'This ad account is not connected in your Organisation yet.',
          tooltipAction: 'Connect this ad account to use Insights with it.',
        },
        connected: {
          ctaText: 'Connected',
          tooltipText:
            'This ad account is already connected in your Organisation.',
          tooltipAction: "You don't need to re-connect it.",
        },
        reConnectionRequired: {
          ctaText: 'Re-connection Required',
          tooltipText:
            'The ad account has become disabled due authentication expiration or other events.',
          tooltipAction:
            'Re-connect this ad account to use Insights with it again.',
        },
        noAdminAccess: {
          ctaText: 'No admin access',
          tooltipText: 'You do not have admin access to this ad account.',
          tooltipAction:
            'Ask an admin of the ad account to connect it or to make you an admin in Facebook Ads Manager.',
        },
      },
    },
  },
  creativeType,
  mediaPreview: {
    showDetails: 'Show details',
  },
  sidebar: {
    facebook: {
      cta: 'Connect Ad Accounts',
      title: 'Accounts',
      noAccounts: 'No Accounts',
      manage: { primaryText: 'Accounts', secondaryText: 'Manage' },
      overview: 'Overview',
      analysis: 'Copilot',
      compare: 'Compare',
      quotaAlert: '{quota}/{quota} connections used.',
    },
    tikTok: {
      cta: 'Connect Ad Accounts',
      title: 'Accounts',
      noAccounts: 'No Accounts',
      manage: { primaryText: 'Accounts', secondaryText: 'Manage' },
      overview: 'Overview',
      analysis: 'Copilot',
      compare: 'Compare',
      quotaAlert: '{quota}/{quota} connections used.',
    },
    reports: {
      sort: {
        az: 'Title (A-Z)',
        za: 'Title (Z-A)',
        createdFirst: 'Created (Newest First)',
        createdLast: 'Created (Oldest First)',
        modifiedFirst: 'Last Modified',
      },
      title: 'Reports',
      createFolder: 'Create Folder',
      deleteFolderWarning:
        'All reports in this folder will be deleted. Are you sure you want to continue?',
      paywall: {
        unlock: 'Upgrade to view more Insights Reports',
      },
    },
    mobile: {
      closeText: 'Close sidebar',
    },
  },
  list: {
    emptyAccounts: {
      title:
        'Plug in your ad account, we’ll tell you what works, why and what to make next',
      cta: 'Connect Ad Account',
      privacy: 'Your data is secure and is strictly private',
      noAccountAccess: 'No account access',
      askWorkspaceUser:
        'You don’t have access to any accounts yet. To view and create reports, ask a workspace user to add you to the relevant accounts.',
      features: {
        aiPoweredRecs: 'AI Powered Recommendations',
        automaticSlackReporting: 'Automatic Slack Reporting',
        aiCreativeTagging: 'AI Creative Tagging',
        prebuiltCreativeReporting: 'Prebuilt Creative Reporting',
        visualCreativeData: 'Visual Creative Data',
      },
    },
    emptyAds: {
      title: 'Nothing to see here',
      body1: 'Do you have ads? Try refetching',
    },
    refresh: {
      ctaText: 'Sync',
      subtitle: {
        pending: 'Sync started {time} ago...',
        complete: 'Last synced {time} ago',
      },
      banner: {
        complete: 'Sync completed successfully.',
        pending: 'This account is currently being synced with Facebook.',
        failed:
          'The last sync with Facebook {time} ago failed. Please try again in a few minutes.',
        partialFailed:
          "The last sync with Facebook completed {time} ago, but we couldn't get insights for {count} ad{{s}}.",
      },
    },
  },
  effectiveStatus,
  resultsSearch: {
    title: 'Looking for a results metric?',
    body: 'To set up your results metric, you’ll need to use the target objective for the adset. This can be found in the results column inside the Facebook Ads Manager dashboard.',
    websiteLeads: 'Website leads',
  },
  metrics: {
    search: 'Search',
    emptySearch: 'No matching metrics found',
    noResults: 'No results',
    multiSelectHint: 'Select options',
    none: '-',
    summary: 'Totals',
    resultsSearch: {
      title: 'Looking for a results metric?',
      body: 'To set up your results metric, you’ll need to use the target objective for the adset. This can be found in the results column inside the Facebook Ads Manager dashboard.',
      websiteLeads: 'Website leads',
    },
    operationSymbols: {
      '=': '=',
      '!=': '≠',
      '>': '>',
      '>=': '≥',
      '<': '<',
      '<=': '≤',
      between: 'between',
      notBetween: 'not between',
      contains: 'contains',
      notContains: 'not contains',
      startsWith: 'starts with',
      endsWith: 'ends with',
      isSet: 'is set',
      isNotSet: 'is not set',
      in: 'includes',
      notIn: 'does not include',
      withinLast: 'within last',
      withinDatePreset: 'within selected time period',
    } satisfies Record<InsightsMetricOperationV2, string>,
  },
  timePeriod,
  timePeriodTruncated,
  groups: {
    adName: 'Ad Name',
    creative: 'Creative',
    headline: 'Headline',
    copy: 'Copy',
    asset: 'Asset',
    landingPage: 'Landing Page',
  },
  card: {
    subheader: 'View insights',
    menu: {
      viewDetails: 'View Details',
      downloadAsset: 'Download Asset',
      share: 'Share',
    },
    tabs: {
      metrics: 'Metrics',
      relativePerformance: 'Relative Performance',
    },
  },
  details: {
    sidebar: {
      open: 'Show insights',
      close: 'Close insights',
      tabs: {
        insights: {
          title: 'Insights',
        },
      },
    },
    toolbar: {
      createReport: {
        paywall: {
          upgrade: 'Upgrade to create more Insights Reports',
        },
      },
      filter: {
        timePeriod: {
          change: {
            upgrade: 'Upgrade to view more Insights Time Periods',
          },
        },
        sort: {
          change: {
            upgrade: 'Upgrade to change Insights Sort filters',
          },
        },
        display: {
          change: {
            upgrade: 'Upgrade to change Insights Display filters',
          },
        },
        activeMetric: {
          change: {
            upgrade: 'Upgrade to change Insights Metric filters',
          },
        },
      },
    },
    shared: { error: 'Unable to get shared Insights Ad' },
    shareModal: {
      title: 'Share {name}',
      screenReaderLabel: 'Sharing privacy setting for {name}',
      publicDescription: 'Anyone with the link can view this ad.',
      privateDescription:
        'Only team members or users with access can view this ad.',
    },
  },
  reports: {
    update: {
      ctaText: 'Save Report',
      tooltipText: 'Save settings to a report',
      successText: 'Report saved successfully',
    },
    createModal: {
      title: 'Create Insights Report',
      showCtaText: 'Create Report',
      submitCtaText: 'Create',
    },
    renameModal: {
      title: 'Edit {name}',
      submitBtn: 'Save',
      success: 'Successfully updated report',
    },
    duplicateModal: {
      title: 'Duplicate {name}',
      detail: 'Select the account you want to duplicate the report into:',
      submitBtn: 'Duplicate',
      success: 'Successfully duplicated report',
      ctaText: 'Save As',
      tooltipText: 'Save settings to a new report',
    },
    deleteModal: {
      title: 'Delete {name}',
      detail: 'Are you sure you want to delete this report?',
      submitBtn: 'Delete',
      success: 'Successfully deleted report',
    },
    shareModal: {
      title: 'Share {name}',
      screenReaderLabel: 'Sharing privacy setting for {name}',
      publicDescription:
        'Anyone with the link can view this report and the ads included in it.',
      privateDescription:
        'Only team members or users with access can view or edit this report.',
    },
    confirmUnsavedModal: {
      title: 'Unsaved Changes',
      description:
        'Changes made will not be saved. Would you like to continue?',
    },
    resyncAlert:
      'Report was last synced over 12 hours ago, please re-sync to ensure you are viewing the most up-to-date information',
  },
  summarySchedules: {
    sidebar: {
      navLinkPrimaryText: 'Slack Schedules',
      navLinkManageText: 'Manage',
    },
    list: {
      title: 'Manage Schedules',
      body1:
        'Manage your weekly scheduled ad insight summaries delivered straight to your Slack',
      connectSlack: {
        title: 'Connect your Slack workspace',
        body1: 'You need to connect your Slack workspace first',
      },
      empty: {
        title: 'Nothing to see here',
        body1: 'Try creating a new schedule',
      },
    },
    createModal: {
      title: 'Schedule Automated Report Summaries',
      subtitle:
        'Set up automated summaries for your reports on a recurring schedule',
      connectingSlack: 'Connecting to your Slack account...',
      selectReportType: 'Select a Report Type',
      selectReportTypeDescription:
        'Choose the type of automated report you want to schedule for recurring delivery',
      availableTemplates: 'Available Templates',
      templates: {
        accountSummaryReport: 'Account Summary Report',
        creativePerformanceReport: 'Creative Performance Report',
        customMetricsReport: 'Custom Metrics Report',
      },
      selectDataSource: 'Select a Data Source',
      selectDataSourceDescription:
        'Choose where your data will come from. You can modify selected metrics without changing your original data source.',
      success: 'Created successfully',
      ctaText: 'Create',
      upgrade: 'Upgrade to create Slack Summary Schedules for your Insights',
    },
    updateModal: {
      title: "Edit '{summaryName}'",
      success: 'Updated successfully',
      ctaText: 'Update',
      upgrade: 'Upgrade to update Slack Summary Schedules for your Insights',
      loadError: 'Could not fetch Slack Summary Schedule',
      channelUnavailable: 'The selected Slack channel is unavailable',
    },
    deleteModal: {
      title: 'Delete {summaryType}',
      description: 'Are you sure you want to delete this summary schedule?',
      success: 'Deleted successfully',
      confirm: 'Confirm',
      cancel: 'Cancel',
      ctaText: 'Delete',
    },
  },
  attributionWindows,
  account: {
    reauthenticateText:
      "Your Facebook Ad Account needs to be re-authenticated via the 'Accounts' menu by an owner or administrator of the account.",
    delete: {
      title: 'Delete Facebook Ad Account',
      description: 'Delete the Facebook Ad Account from your workspace',
      cta: 'Delete',
      modal: {
        title: 'Delete Facebook Ad Account',
        body: 'Are you sure you want to delete this Facebook Ad Account?',
      },
      success: 'Facebook Ad Account was deleted successfully',
      error: 'Error deleting Facebook Ad Account: {error}',
    },
  },
  reconnectRequiredModal: {
    title: 'Authorisation required',
    body1:
      'This Facebook ad account ({name}) needs to be re-connected to sync ad data. We recommend that an owner or admin of the Facebook ad account performs this action.',
    body2: 'The account was originally connected by this team member:',
    supportCta: 'Contact support',
    connectAccountCta: 'Connect account',
  },
  compare: {
    noData: 'No data to show',
    noGroups: 'Create two groups of ads to compare.',
    addGroup: 'Add Group',
    editFilters: 'Edit Filters',
    advancedFilter: 'Advanced Filter',
    numSpent: '{count} spent',
    comparingNum: 'Comparing {count} ad group{{s}}',
  },
  tags: {
    addTag: 'Add tag',
    countOfSelectedAds: '{count} Ad{{s}} Selected',
    searchPlaceholder: 'Search or create tags',
    categorySearchPlaceholder: 'Search or create categories',
    enter: 'Enter',
    noTagsCreated: 'No tags created yet',
    noTagsFound: 'No tags found',
    noCategoriesFound: 'No categories found',
    categories: 'Categories ({count})',
    recentTags: 'Recent',
    otherTags: 'Other ({count})',
    cancel: 'Cancel',
    save: 'Save',
  },
  groupBreakdown: {
    wizardScoreTitle: 'Top ads by Wizard Scores',
    emptyHookScore:
      'To receive a hook score, there must be a video asset present with over $50 in ad spend.',
    emptyHoldScore:
      'To receive a hold score, there must be a video asset present with over $50 in ad spend.',
    emptyClickScore:
      'To receive a click score, there must be an asset present with over $50 in ad spend.',
    emptyBuyScore:
      'To receive a buy score, there must be an asset present with over $50 in ad spend.',
  },
  table: {
    tableSettings: 'Table Format',
    colorFormatting: 'Color Formatting',
  },
  onboarding: {
    step1Title:
      'You’re only a few steps away from unlocking your insights dashboard.',
    step2Title: 'Your account has been connected.',
    selectPlatform: 'Select your platform',
    connectMeta: 'Track Facebook & Instagram campaigns',
    connectTikTok: 'Track TikTok campaigns',
    connectYouTube: 'Track video and display ad performance',
    connectLinkedIn: 'Track professional audience campaigns',
    connectSnapchat: 'Track Snapchat campaigns',
    chooseAdAccounts: 'Choose ad accounts to connect',
    pleaseConnectTikTok:
      'Please connect your TikTok Business account in the popup window',
    syncInfo:
      'Initial sync will include data from your last 30 days of data, then we’ll help you customise your key metrics.',
    connectingAccount: 'Connecting your account...',
    creatingCustomDashboard: 'Creating your custom dashboard...',
  },
};
